'use client'

import { useEffect, useRef, useState } from 'react'

// Decorative motion for static signup cards. The caller keeps its real image
// underneath; no video source is attached until someone intentionally previews it.
const HoverVideoPreview = ({ src, className = '' }) => {
  const hostRef = useRef(null)
  const videoRef = useRef(null)
  const [active, setActive] = useState(false)
  const [readySource, setReadySource] = useState(null)

  useEffect(() => {
    const target = hostRef.current?.closest('a[href]')
    if (!target || !src) return

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const hover = window.matchMedia('(any-hover: hover)')
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection
    const bounds = target.getBoundingClientRect()
    let inView =
      bounds.bottom > 0 &&
      bounds.top < window.innerHeight &&
      bounds.right > 0 &&
      bounds.left < window.innerWidth
    let hovered = false
    let focused = false
    let timer = null

    const sync = () => {
      window.clearTimeout(timer)
      const limitedConnection =
        connection?.saveData ||
        ['slow-2g', '2g', '3g'].includes(connection?.effectiveType)
      const allowed =
        inView &&
        !document.hidden &&
        !motion.matches &&
        !limitedConnection &&
        ((hovered && hover.matches) || focused)

      if (allowed) {
        // Avoid fetching videos while the pointer merely passes over a feed.
        timer = window.setTimeout(() => setActive(true), 120)
      } else {
        setReadySource(null)
        setActive(false)
      }
    }
    const enter = (event) => {
      hovered = event.pointerType === 'mouse' || event.pointerType === 'pen'
      sync()
    }
    const leave = () => {
      hovered = false
      sync()
    }
    const focus = () => {
      // A touch-generated focus must still be a single normal signup tap.
      focused = target.matches(':focus-visible')
      sync()
    }
    const blur = (event) => {
      if (!target.contains(event.relatedTarget)) focused = false
      sync()
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting && entry.intersectionRatio > 0
        sync()
      },
      { threshold: 0.01 },
    )

    target.addEventListener('pointerenter', enter)
    target.addEventListener('pointerleave', leave)
    target.addEventListener('focusin', focus)
    target.addEventListener('focusout', blur)
    document.addEventListener('visibilitychange', sync)
    motion.addEventListener('change', sync)
    hover.addEventListener('change', sync)
    connection?.addEventListener?.('change', sync)
    observer.observe(target)

    return () => {
      window.clearTimeout(timer)
      observer.disconnect()
      target.removeEventListener('pointerenter', enter)
      target.removeEventListener('pointerleave', leave)
      target.removeEventListener('focusin', focus)
      target.removeEventListener('focusout', blur)
      document.removeEventListener('visibilitychange', sync)
      motion.removeEventListener('change', sync)
      hover.removeEventListener('change', sync)
      connection?.removeEventListener?.('change', sync)
    }
  }, [src])

  useEffect(() => {
    const video = videoRef.current
    if (!active || !video) return
    let disposed = false
    let videoFrame = null
    let animationFrame = null
    setReadySource(null)

    const showFrame = () => {
      if (!disposed && !video.paused && video.readyState >= 2)
        setReadySource(src)
    }
    const playing = () => {
      if (video.requestVideoFrameCallback) {
        videoFrame = video.requestVideoFrameCallback(showFrame)
      } else {
        animationFrame = window.requestAnimationFrame(showFrame)
      }
    }
    const hide = () => {
      if (!disposed) setReadySource(null)
    }
    const failed = () => {
      if (!disposed) {
        setReadySource(null)
        setActive(false)
      }
    }

    video.addEventListener('playing', playing)
    video.addEventListener('pause', hide)
    video.addEventListener('error', failed)
    video.muted = true
    // Own the source in the same effect as cleanup so effect replay or a changed
    // portrait cannot leave a reused video element with its new source removed.
    video.src = src
    const playback = video.play()
    playback?.catch(failed)

    return () => {
      disposed = true
      video.removeEventListener('playing', playing)
      video.removeEventListener('pause', hide)
      video.removeEventListener('error', failed)
      if (videoFrame !== null) video.cancelVideoFrameCallback?.(videoFrame)
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame)
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
  }, [active, src])

  return (
    <span
      ref={hostRef}
      className="contents pointer-events-none"
      aria-hidden="true"
    >
      {active && (
        <video
          key={src}
          ref={videoRef}
          muted
          loop
          playsInline
          preload="none"
          tabIndex={-1}
          data-hover-video
          className={`pointer-events-none ${className}`}
          style={{
            opacity: readySource === src ? 1 : 0,
            transition: 'opacity 240ms ease',
          }}
        />
      )}
    </span>
  )
}

export default HoverVideoPreview
